import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSave, FiX } from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import inventoryService from "../../services/inventoryService";

const UNIT_OPTIONS = [
    "pcs",
    "kg",
    "g",
    "litre",
    "ml",
    "box",
    "ream",
    "set",
    "pair",
    "roll",
    "bottle",
    "packet",
    "sheet",
    "dozen",
];

const EMPTY_FORM = {
    name: "",
    itemCode: "",
    category: "",
    description: "",
    unit: "pcs",
    minStockLevel: "",
    unitPrice: "",
    location: "",
    notes: "",
};

function Field({ label, required, error, children }) {
    return (
        <div className="form-group">
            <label className="form-label">
                {label}
                {required && <span style={{ color: "var(--danger)" }}> *</span>}
            </label>
            {children}
            {error && <span className="form-error">{error}</span>}
        </div>
    );
}

export default function ItemForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);
    const { academicYear } = useApp();

    const [form, setForm] = useState(EMPTY_FORM);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEdit);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        inventoryService
            .getCategories(academicYear?._id ? { academicYear: academicYear._id } : {})
            .then((r) => setCategories(r.data))
            .catch(() => { });
    }, [academicYear?._id]);

    useEffect(() => {
        if (!isEdit) return;
        setFetching(true);
        inventoryService
            .getItemById(id)
            .then((res) => {
                const d = res.data;
                setForm({
                    name: d.name,
                    itemCode: d.itemCode || "",
                    category: d.category?._id || "",
                    description: d.description || "",
                    unit: d.unit,
                    minStockLevel: d.minStockLevel ?? "",
                    unitPrice: d.unitPrice ?? "",
                    location: d.location || "",
                    notes: d.notes || "",
                });
            })
            .catch((err) => {
                toast.error(err.message);
                navigate("/inventory/items");
            })
            .finally(() => setFetching(false));
    }, [id, isEdit, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((p) => ({ ...p, [name]: value }));
        setErrors((p) => ({ ...p, [name]: "" }));
    };

    const validate = () => {
        const errs = {};
        if (!form.name.trim()) errs.name = "Name is required.";
        if (!form.category) errs.category = "Category is required.";
        if (!form.unit) errs.unit = "Unit is required.";
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);
        try {
            const payload = {
                ...form,
                academicYear: academicYear?._id,
                minStockLevel: Number(form.minStockLevel) || 0,
                unitPrice: Number(form.unitPrice) || 0,
            };
            if (isEdit) {
                const res = await inventoryService.updateItem(id, payload);
                toast.success(res.message);
                navigate(`/inventory/items/${id}`);
            } else {
                const res = await inventoryService.createItem(payload);
                toast.success(res.message);
                navigate(`/inventory/items/${res.data._id}`);
            }
        } catch (err) {
            toast.error(err.message || "Failed to save item.");
        } finally {
            setLoading(false);
        }
    };

    if (fetching)
        return (
            <div className="loading-center">
                <div className="spinner" />
                Loading…
            </div>
        );

    return (
        <div>
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div>
                    <h1 className="page-title">{isEdit ? "Edit Item" : "Add Item"}</h1>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button
                        className="btn btn-outline"
                        onClick={() => navigate("/inventory/items")}
                    >
                        <FiX size={14} /> Cancel
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        <FiSave size={14} />{" "}
                        {loading ? "Saving…" : isEdit ? "Update Item" : "Save Item"}
                    </button>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="card" style={{ marginBottom: "1rem" }}>
                    <div className="card-header">Item Details</div>
                    <div className="card-body">
                        <div className="form-grid">
                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">
                                    Item Name <span style={{ color: "var(--danger)" }}>*</span>
                                </label>
                                <input
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    className={`form-control ${errors.name ? "error" : ""}`}
                                    placeholder="e.g. Microscope Slides"
                                    autoFocus
                                />
                                {errors.name && (
                                    <span className="form-error">{errors.name}</span>
                                )}
                            </div>

                            <Field label="Item Code">
                                <input
                                    name="itemCode"
                                    value={form.itemCode}
                                    onChange={handleChange}
                                    className="form-control"
                                    placeholder="e.g. LAB-001"
                                />
                            </Field>

                            <Field label="Category" required error={errors.category}>
                                <select
                                    name="category"
                                    value={form.category}
                                    onChange={handleChange}
                                    disabled={isEdit}
                                    className={`form-control ${errors.category ? "error" : ""}`}
                                >
                                    <option value="">Select category…</option>
                                    {categories.map((c) => (
                                        <option key={c._id} value={c._id}>
                                            {c.name}
                                        </option>
                                    ))}
                                </select>
                            </Field>

                            <Field label="Unit of Measure" required error={errors.unit}>
                                <select
                                    name="unit"
                                    value={form.unit}
                                    onChange={handleChange}
                                    className="form-control"
                                >
                                    {UNIT_OPTIONS.map((u) => (
                                        <option key={u} value={u}>
                                            {u}
                                        </option>
                                    ))}
                                </select>
                            </Field>

                            <Field label="Min Stock Level (Alert Threshold)">
                                <input
                                    type="number"
                                    min="0"
                                    name="minStockLevel"
                                    value={form.minStockLevel}
                                    onChange={handleChange}
                                    className="form-control"
                                    placeholder="e.g. 10"
                                />
                                <span className="form-hint">
                                    Alert shows when stock ≤ this level
                                </span>
                            </Field>

                            <Field label="Unit Price (₹)">
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    name="unitPrice"
                                    value={form.unitPrice}
                                    onChange={handleChange}
                                    className="form-control"
                                    placeholder="e.g. 250"
                                />
                            </Field>

                            <Field label="Storage Location">
                                <input
                                    name="location"
                                    value={form.location}
                                    onChange={handleChange}
                                    className="form-control"
                                    placeholder="e.g. Lab 2 — Shelf B"
                                />
                            </Field>

                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">Description</label>
                                <textarea
                                    name="description"
                                    value={form.description}
                                    onChange={handleChange}
                                    className="form-control"
                                    rows={2}
                                    style={{ resize: "vertical" }}
                                    placeholder="Optional description…"
                                />
                            </div>

                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">Notes</label>
                                <textarea
                                    name="notes"
                                    value={form.notes}
                                    onChange={handleChange}
                                    className="form-control"
                                    rows={2}
                                    style={{ resize: "vertical" }}
                                    placeholder="Any additional notes…"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {!isEdit && (
                    <div
                        style={{
                            background: "#e8f1f8",
                            border: "1px solid #b6d5ec",
                            borderRadius: 8,
                            padding: "0.75rem 1rem",
                            fontSize: 13,
                            color: "#002747",
                        }}
                    >
                        💡 Initial stock can be added after creating the item using a{" "}
                        <strong>Stock In</strong> transaction.
                    </div>
                )}
            </form>
        </div>
    );
}
